import React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  getClientBackgroundOptions,
  resolveBackgroundImageUrl,
} from "../../../../Background/backgroundImageOptions";

const BrandingStep = ({
  formData,
  updateFormData,
  client,
  getAuthHeaders,
  fieldErrors = {},
}) => {
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [backgrounds, setBackgrounds] = useState([]);
  const normalizedClient = String(client || "").toLowerCase();

  const selectedFolder = formData?.reveal?.logo?.folder || "";
  const selectedImage = formData?.reveal?.logo?.image || "";

  const selectedLogoUrl =
    normalizedClient === "delta" && selectedFolder && selectedImage
      ? `/brand-assets/Delta/${selectedFolder}/${selectedImage}`
      : "";
  const resolvedBackgroundImageUrl = resolveBackgroundImageUrl(
    formData.backgroundImage
  );
  const clientBackgroundOptions = getClientBackgroundOptions(normalizedClient);

  useEffect(() => {
    if (normalizedClient !== "delta") return;
    const fetchFolders = async () => {
      try {
        const res = await axios.get(
          "/api/assets/delta/folders",
          getAuthHeaders()
        );
        setFolders(res.data.folders || []);
      } catch (e) {
        console.error("Failed to load Delta folders", e);
      }
    };
    const fetchBackgrounds = async () => {
      try {
        const res = await axios.get(
          "/api/assets/delta/backgrounds",
          getAuthHeaders()
        );
        setBackgrounds(res.data.images || []);
      } catch (e) {
        console.error("Failed to load Delta backgrounds", e);
      }
    };
    fetchFolders();
    fetchBackgrounds();
  }, [normalizedClient, getAuthHeaders]);

  useEffect(() => {
    if (normalizedClient !== "delta" || !selectedFolder) {
      setImages([]);
      return;
    }
    const fetchImages = async () => {
      try {
        const res = await axios.get(
          `/api/assets/delta/images?folder=${encodeURIComponent(
            selectedFolder
          )}`,
          getAuthHeaders()
        );
        setImages(res.data.images || []);
      } catch (e) {
        console.error("Failed to load Delta images", e);
      }
    };
    fetchImages();
  }, [normalizedClient, selectedFolder, getAuthHeaders]);

  const setLogo = (next) => {
    updateFormData("reveal", {
      ...(formData.reveal || {}),
      logo: { ...(formData.reveal?.logo || {}), ...next },
    });
  };

  const gradientBackground = `linear-gradient(to bottom, #FFFFFF 0%, ${
    formData.backgroundColor || "#FFFFFF"
  } 100%)`;

  return (
    <div className="step-content">
      <div className="form-group">
        <label>BACKGROUND COLOR</label>
        <div className="color-input-group">
          <input
            type="color"
            value={formData.backgroundColor}
            onChange={(e) => updateFormData("backgroundColor", e.target.value)}
            className="color-picker"
          />
          <input
            type="text"
            value={formData.backgroundColor}
            onChange={(e) =>
              updateFormData("backgroundColor", e.target.value.trim())
            }
            onBlur={(e) =>
              updateFormData(
                "backgroundColor",
                (() => {
                  const v = String(e.target.value || "").trim();
                  if (/^#[0-9a-fA-F]{6}$/.test(v)) return v;
                  if (/^[0-9a-fA-F]{6}$/.test(v)) return `#${v}`;
                  const m3 = v.match(
                    /^#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])$/
                  );
                  if (m3)
                    return `#${m3[1]}${m3[1]}${m3[2]}${m3[2]}${m3[3]}${m3[3]}`;
                  return v;
                })()
              )
            }
            placeholder="#FFFFFF"
            className="hex-input"
          />
        </div>
        <small className="field-note">*Hex Code</small>
      </div>
      <div className="form-group gradient-toggle-group">
        <label>MAKE GRADIENT?</label>
        <div className="gradient-toggle-control">
          <button
            type="button"
            className={`toggle-switch ${formData.makeGradient ? "on" : ""}`}
            onClick={() => updateFormData("makeGradient", !formData.makeGradient)}
            aria-pressed={Boolean(formData.makeGradient)}
            aria-label="Toggle gradient background"
          >
            <span className="toggle-thumb" />
          </button>
          <span className="toggle-value">
            {formData.makeGradient ? "Yes" : "No"}
          </span>
        </div>
        <small className="field-note">
          Applies when no background image is selected.
        </small>
      </div>

      {normalizedClient === "delta" && (
        <>
          <div className="form-group">
            <label>DELTA LOGO FAMILY</label>
            <select
              value={selectedFolder}
              onChange={(e) => {
                const folder = e.target.value;
                setLogo({ folder, image: "" });
              }}
            >
              <option value="">Default</option>
              {folders.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>

          {selectedFolder && (
            <div className="form-group">
              <label>DELTA LOGO IMAGE</label>
              <select
                value={selectedImage}
                onChange={(e) => setLogo({ image: e.target.value })}
                disabled={!selectedFolder}
              >
                <option value="">Select an image</option>
                {images.map((img) => (
                  <option key={img.name} value={img.name}>
                    {img.name}
                  </option>
                ))}
              </select>
              {fieldErrors.logo && (
                <div className="field-error">{fieldErrors.logo}</div>
              )}
              {selectedLogoUrl ? (
                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: 8,
                    padding: "16px 8px",
                    backgroundColor: formData.backgroundColor,
                    backgroundImage: resolvedBackgroundImageUrl
                      ? `url(${resolvedBackgroundImageUrl})`
                      : formData.makeGradient
                      ? gradientBackground
                      : undefined,
                    backgroundSize: resolvedBackgroundImageUrl
                      ? "cover"
                      : undefined,
                    backgroundPosition: resolvedBackgroundImageUrl
                      ? "center"
                      : undefined,
                  }}
                >
                  <img
                    src={selectedLogoUrl}
                    alt="Selected logo"
                    style={{ maxHeight: 40 }}
                  />
                </div>
              ) : null}
            </div>
          )}

          <div className="form-group">
            <label>DELTA BACKGROUND IMAGE</label>
            <select
              value={formData.backgroundImage || ""}
              onChange={(e) =>
                updateFormData("backgroundImage", e.target.value)
              }
            >
              <option value="">None</option>
              {backgrounds.map((bg) => (
                <option key={bg.name} value={bg.url}>
                  {bg.name.replace(/\.[^.]+$/, "")}
                </option>
              ))}
            </select>
          </div>
        </>
      )}
      {normalizedClient === "airfrance" && (
        <div className="form-group">
          <label>AIR FRANCE BACKGROUND IMAGE</label>
          <select
            value={formData.backgroundImage || ""}
            onChange={(e) => updateFormData("backgroundImage", e.target.value)}
          >
            <option value="">None</option>
            {clientBackgroundOptions.map((bg) => (
              <option key={bg.value} value={bg.value}>
                {bg.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {/* 
      <div className="form-group">
        <label>BACKGROUND IMAGE</label>
        <div className="image-upload-group">
          <input
            type="url"
            value={formData.backgroundImage}
            onChange={(e) => updateFormData("backgroundImage", e.target.value)}
            placeholder="https://example.com/background.jpg"
            className="url-input"
          />
        </div>
      </div> */}

      {/* <div className="form-group">
        <label>BANNER IMAGE</label>
        <div className="image-upload-group">
          <input
            type="url"
            value={formData.bannerImage}
            onChange={(e) => updateFormData("bannerImage", e.target.value)}
            placeholder="https://example.com/banner.jpg"
            className="url-input"
          />
        </div>
      </div> */}
    </div>
  );
};

export default BrandingStep;
